import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import { useUiPreference } from '@ctw/shared/hooks/use-ui-preference';
import type { NormalizedUnitSystem } from '@zeus-health/types';
import { useCallback } from 'react';

export type UnitSystemPreference = NormalizedUnitSystem | 'original';

export const UNIT_SYSTEM_PREFERENCE_OPTIONS: Array<{ key: UnitSystemPreference; name: string }> = [
  { key: 'original', name: 'Original' },
  { key: 'imperial', name: 'Imperial' },
  { key: 'metric', name: 'Metric' },
];

const UNIT_SYSTEM_PREFERENCE_KEY = 'vitalsUnitSystem';

const isUnitSystemPreference = (value: string): value is UnitSystemPreference =>
  UNIT_SYSTEM_PREFERENCE_OPTIONS.some((option) => option.key === value);

/**
 * The unit system to display vitals in: the user's stored choice, else the builder config's
 * `normalizedUnitSystem`. `unitSystem` is `undefined` for "original" (no conversion).
 */
export const useUnitSystemPreference = () => {
  const { normalizedUnitSystem } = useBuilderConfig();
  const configuredPreference: UnitSystemPreference = normalizedUnitSystem ?? 'original';
  const [storedPreference, setStoredPreference] = useUiPreference(
    UNIT_SYSTEM_PREFERENCE_KEY,
    configuredPreference,
  );

  const preference = isUnitSystemPreference(storedPreference)
    ? storedPreference
    : configuredPreference;
  const setPreference = useCallback(
    (next: UnitSystemPreference) => setStoredPreference(next),
    [setStoredPreference],
  );

  return {
    preference,
    setPreference,
    unitSystem: preference === 'original' ? undefined : preference,
  };
};
